import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Check, ChevronLeft, ChevronRight, Loader2,
  Phone, Clock, ShieldCheck, Image as ImageIcon, ClipboardCheck, Sparkles,
  Plus, Trash2, ImagePlus, FileText, UploadCloud, MapPin,
} from "lucide-react";
import {
  createDiagnosticLab, uploadLabPhoto, uploadLabDocument, geocodeLabAddress, saveLabOperations,
  LAB_TYPE_OPTIONS, LAB_TYPES_REQUIRING_MODALITIES,
  CONTACT_TYPE_OPTIONS, CONTACT_TYPES_WITH_PAN, CONTACT_TYPES_WITH_WHATSAPP,
  LAB_PHOTO_SLOTS, LAB_GALLERY_MAX,
  LAB_DOC_TYPES, LAB_DOC_KYC_MAX, LAB_DOC_ACCEPT,
  SPECIALIZATION_OPTIONS, MODALITY_OPTIONS, LANGUAGE_OPTIONS, labelFor,
  validateEstablishedYear, validatePlace, validateEmail, validateMobile, validatePan,
} from "../services/diagnosticLabsService";
import { SegmentedControl, ChipMultiSelect, AsyncChainSelect, PincodeAutoAddress, LocationPicker, Toast, getCurrentRole } from "../components/diagnostic/primitives";
import OperationsForm, {
  emptyOperations, hasOperationsInput, validateOperations, buildOperationsPayload, operationsSummary,
} from "../components/diagnostic/OperationsForm";
import { useBlurFieldErrors } from "../components/diagnostic/useBlurFieldErrors";

const STEPS = [
  { key: "identity", label: "Identity", icon: Sparkles },
  { key: "contact", label: "Contact & Address", icon: Phone },
  { key: "operations", label: "Operations", icon: Clock },
  { key: "documents", label: "Documents", icon: ShieldCheck },
  { key: "media", label: "Media", icon: ImageIcon },
  { key: "review", label: "Review & Publish", icon: ClipboardCheck },
];

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50";

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
      {hint && <span className="ml-1 text-xs font-normal text-gray-400">{hint}</span>}
    </label>
    {children}
  </div>
);

/* -------------------------------- wizard -------------------------------- */
const DiagnosticLabWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || "/subscriber-wallet/labs/labs";
  const currentRole = getCurrentRole();
  const canAccess = currentRole === "Ops" || currentRole === "SuperAdmin";

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [opsErrors, setOpsErrors] = useState({});
  const [opsTouched, setOpsTouched] = useState(false);
  const blur = useBlurFieldErrors();
  const ck = (i, field) => `contact_${i}_${field}`;
  const [form, setForm] = useState({
    business_name: "", brand_name: "", lab_type: "", established_year: "",
    chain: null, chainLabel: "", specializations: [], modalities: [],
    description: "", tagline: "", languages: [],
    service_model: "local", // "local" (address + radius) | "national" (serviceable pincodes)
    address_line: "", landmark: "", locality: "", city: "", state: "", pincode: "", latitude: "", longitude: "",
    contacts: [],
    photos: {},      // { [slotKey]: { file, preview } }
    gallery: [],     // [{ file, preview }]
    documents: {},   // { [docKey]: { file, name } | owner_kyc: [{ file, name }] }
    operations: emptyOperations(),
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // ----- Contacts (sent in the create payload) -----
  const emptyContact = { type: "", name: "", mobile: "", email: "", pan: "", whatsapp: "", is_primary: false };
  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, { ...emptyContact }] }));
  const removeContact = (idx) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }));
  const updateContact = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      contacts: f.contacts.map((c, i) => {
        if (i !== idx) return key === "is_primary" && value ? { ...c, is_primary: false } : c;
        return { ...c, [key]: value };
      }),
    }));

  // ----- Photos (held locally, uploaded after the lab is created) -----
  const PHOTO_MAX = 2 * 1024 * 1024;
  const IMG_EXT = [".png", ".jpg", ".jpeg"];
  const validImage = (file) => {
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!IMG_EXT.includes(ext)) { showToast("error", "Image must be a PNG or JPEG file."); return false; }
    if (file.size > PHOTO_MAX) { showToast("error", "Image must be 2MB or smaller."); return false; }
    return true;
  };
  const setSinglePhoto = (key, file) => {
    if (!file || !validImage(file)) return;
    setForm((f) => {
      if (f.photos[key]?.preview) URL.revokeObjectURL(f.photos[key].preview);
      return { ...f, photos: { ...f.photos, [key]: { file, preview: URL.createObjectURL(file) } } };
    });
  };
  const removeSinglePhoto = (key) =>
    setForm((f) => {
      if (f.photos[key]?.preview) URL.revokeObjectURL(f.photos[key].preview);
      const next = { ...f.photos };
      delete next[key];
      return { ...f, photos: next };
    });
  const addGalleryPhoto = (file) => {
    if (!file || !validImage(file)) return;
    setForm((f) =>
      f.gallery.length >= LAB_GALLERY_MAX
        ? f
        : { ...f, gallery: [...f.gallery, { file, preview: URL.createObjectURL(file) }] });
  };
  const removeGalleryPhoto = (idx) =>
    setForm((f) => {
      if (f.gallery[idx]?.preview) URL.revokeObjectURL(f.gallery[idx].preview);
      return { ...f, gallery: f.gallery.filter((_, i) => i !== idx) };
    });

  // ----- Documents (held locally, uploaded after the lab is created) -----
  const DOC_MAX = 10 * 1024 * 1024;
  const DOC_EXT = [".pdf", ".jpg", ".jpeg", ".png"];
  const validDoc = (file) => {
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!DOC_EXT.includes(ext)) { showToast("error", "File must be PDF / JPG / PNG."); return false; }
    if (file.size > DOC_MAX) { showToast("error", "File must be 10MB or smaller."); return false; }
    return true;
  };
  const setSingleDoc = (key, file) => {
    if (!file || !validDoc(file)) return;
    setForm((f) => ({ ...f, documents: { ...f.documents, [key]: { file, name: file.name } } }));
  };
  const removeSingleDoc = (key) =>
    setForm((f) => {
      const next = { ...f.documents };
      delete next[key];
      return { ...f, documents: next };
    });
  const addKycDoc = (file) => {
    if (!file || !validDoc(file)) return;
    setForm((f) => {
      const list = f.documents.owner_kyc || [];
      return list.length >= LAB_DOC_KYC_MAX
        ? f
        : { ...f, documents: { ...f.documents, owner_kyc: [...list, { file, name: file.name }] } };
    });
  };
  const removeKycDoc = (idx) =>
    setForm((f) => ({
      ...f,
      documents: { ...f.documents, owner_kyc: (f.documents.owner_kyc || []).filter((_, i) => i !== idx) },
    }));
  const needsModalities = LAB_TYPES_REQUIRING_MODALITIES.includes(form.lab_type);
  const descLen = form.description.length;
  const tagLen = form.tagline.length;
  const docCount = LAB_DOC_TYPES.reduce(
    (a, dt) => a + (dt.multi ? (form.documents[dt.key] || []).length : form.documents[dt.key] ? 1 : 0), 0);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3200); };

  const handleGeocode = async () => {
    setGeoBusy(true);
    try {
      const resp = await geocodeLabAddress(currentRole, {
        address_line: form.address_line, locality: form.locality, landmark: form.landmark,
        city: form.city, state: form.state, pincode: form.pincode,
      });
      const d = resp?.msg || resp;
      if (d?.latitude && d?.longitude) {
        setForm((f) => ({ ...f, latitude: d.latitude, longitude: d.longitude }));
        showToast("success", "Coordinates fetched.");
      } else {
        showToast("error", "Couldn't fetch coordinates for this address.");
      }
    } catch (err) {
      showToast("error", err.message || "Geocoding failed.");
    } finally {
      setGeoBusy(false);
    }
  };

  const validateIdentity = () => {
    const e = identityErrors();
    blur.touchMany(["brand_name", "business_name", "lab_type", "modalities", "languages", "description", "tagline", "established_year"]);
    return !Object.values(e).some(Boolean);
  };

  const identityErrors = () => {
    const e = {};
    if (form.business_name.trim().length < 3) e.business_name = "At least 3 characters.";
    if (!form.brand_name.trim()) e.brand_name = "Brand name is required.";
    if (!form.lab_type) e.lab_type = "Select a lab type.";
    if (needsModalities && form.modalities.length === 0) e.modalities = "Select at least one modality.";
    if (form.languages.length === 0) e.languages = "Select at least one language.";
    if (descLen > 500) e.description = "Max 500 characters.";
    if (tagLen > 80) e.tagline = "Max 80 characters.";
    const yearErr = validateEstablishedYear(form.established_year);
    if (yearErr) e.established_year = yearErr;
    return e;
  };

  const latErr = () => (
    form.latitude && (Number.isNaN(Number(form.latitude)) || Math.abs(Number(form.latitude)) > 90)
      ? "Latitude must be a number between -90 and 90."
      : ""
  );
  const lngErr = () => (
    form.longitude && (Number.isNaN(Number(form.longitude)) || Math.abs(Number(form.longitude)) > 180)
      ? "Longitude must be a number between -180 and 180."
      : ""
  );

  const contactFieldErrors = (i, c) => {
    const label = `Contact ${i + 1}`;
    const hasData = c.name || c.mobile || c.email || c.pan || c.whatsapp || c.type;
    if (!hasData) return {};
    const e = {};
    if (!c.name?.trim()) e[ck(i, "name")] = `${label}: name is required.`;
    if (!c.mobile?.trim()) e[ck(i, "mobile")] = `${label}: mobile is required.`;
    else if (validateMobile(c.mobile)) e[ck(i, "mobile")] = `${label}: ${validateMobile(c.mobile)}`;
    if (validateEmail(c.email)) e[ck(i, "email")] = `${label}: ${validateEmail(c.email)}`;
    if (validatePan(c.pan)) e[ck(i, "pan")] = `${label}: ${validatePan(c.pan)}`;
    if (validateMobile(c.whatsapp, "WhatsApp")) e[ck(i, "whatsapp")] = `${label}: ${validateMobile(c.whatsapp, "WhatsApp")}`;
    return e;
  };

  const allContactErrors = () => form.contacts.reduce((acc, c, i) => ({ ...acc, ...contactFieldErrors(i, c) }), {});

  // Step 1 — address (city/state) + per-contact format checks. Returns first message.
  const validateContactAddress = () => {
    const contactErrs = allContactErrors();
    // National labs serve by pincode (uploaded on the detail page after creation),
    // not a single address — skip the address/geo checks for them.
    const isNational = form.service_model === "national";
    blur.touchMany([...(isNational ? [] : ["city", "state", "latitude", "longitude"]), ...Object.keys(contactErrs)]);
    if (!isNational) {
      const cityErr = validatePlace(form.city, "City");
      if (cityErr) return cityErr;
      const stateErr = validatePlace(form.state, "State");
      if (stateErr) return stateErr;
      const lat = latErr();
      if (lat) return lat;
      const lng = lngErr();
      if (lng) return lng;
    }
    const firstContactErr = Object.values(contactErrs)[0];
    if (firstContactErr) return firstContactErr;
    return "";
  };

  const validateOperationsStep = () => {
    if (!hasOperationsInput(form.operations)) {
      setOpsErrors({});
      return true;
    }
    const e = validateOperations(form.operations);
    setOpsErrors(e);
    setOpsTouched(true);
    if (Object.keys(e).length) {
      showToast("error", Object.values(e)[0]);
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (step === 0 && !validateIdentity()) { showToast("error", "Please fix the highlighted fields."); return; }
    if (step === 1) {
      const msg = validateContactAddress();
      if (msg) { showToast("error", msg); return; }
    }
    if (step === 2 && !validateOperationsStep()) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  // Photos can only attach once the lab (Vendor) exists — upload post-create.
  const uploadPhotos = async (labId) => {
    const tasks = [
      ...Object.entries(form.photos).filter(([, p]) => p?.file).map(([key, p]) => ({ type: key, file: p.file })),
      ...form.gallery.filter((g) => g?.file).map((g) => ({ type: "gallery", file: g.file })),
    ];
    let failed = 0;
    for (const t of tasks) {
      try { await uploadLabPhoto(currentRole, labId, t.type, t.file); }
      catch { failed += 1; }
    }
    return failed;
  };

  const uploadDocuments = async (labId) => {
    const tasks = [];
    LAB_DOC_TYPES.forEach((dt) => {
      if (dt.multi) (form.documents[dt.key] || []).forEach((d) => d?.file && tasks.push({ type: dt.key, file: d.file }));
      else if (form.documents[dt.key]?.file) tasks.push({ type: dt.key, file: form.documents[dt.key].file });
    });
    let failed = 0;
    for (const t of tasks) {
      try { await uploadLabDocument(currentRole, labId, t.type, t.file); }
      catch { failed += 1; }
    }
    return failed;
  };

  const opsReviewLabel = () => {
    if (!hasOperationsInput(form.operations)) return "Skipped — configure later on detail page";
    const sum = operationsSummary(buildOperationsPayload(form.operations));
    if (!sum) return "Configured";
    const modes = [sum.home && "Home collection", sum.walkin && "Walk-in"].filter(Boolean).join(" · ");
    const days = sum.openDays.length ? `${sum.openDays.join(", ")} open` : "No days selected";
    return [modes, days, sum.reportTat != null && sum.reportTat !== "" ? `TAT ${sum.reportTat}h` : ""].filter(Boolean).join(" · ");
  };

  const handlePublish = async () => {
    if (!validateIdentity()) { setStep(0); showToast("error", "Please complete Identity."); return; }
    const caErr = validateContactAddress();
    if (caErr) { setStep(1); showToast("error", caErr); return; }
    if (!validateOperationsStep()) { setStep(2); return; }
    setSubmitting(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        brand_name: form.brand_name.trim(),
        lab_type: form.lab_type,
        established_year: form.established_year?.trim() || null,
        chain: form.chain || null,
        specializations: form.specializations,
        modalities: form.modalities,
        description: form.description.trim(),
        tagline: form.tagline.trim(),
        languages: form.languages,
        service_model: form.service_model,
        // National labs have no single address — serviceable pincodes are uploaded
        // on the detail page after creation. Send address fields blank for them.
        ...(form.service_model === "national"
          ? {
              address_line: "", landmark: "", locality: "", city: "", state: "", pincode: "",
              latitude: null, longitude: null,
            }
          : {
              address_line: form.address_line.trim(),
              landmark: form.landmark.trim(),
              locality: form.locality.trim(),
              city: form.city.trim(),
              state: form.state.trim(),
              pincode: form.pincode.trim(),
              latitude: form.latitude !== "" && form.latitude != null ? Number(form.latitude) : null,
              longitude: form.longitude !== "" && form.longitude != null ? Number(form.longitude) : null,
            }),
        contacts: form.contacts
          .map((c) => ({
            type: c.type || null,
            name: (c.name || "").trim(),
            mobile: (c.mobile || "").trim(),
            email: (c.email || "").trim(),
            pan: (c.pan || "").trim(),
            whatsapp: (c.whatsapp || "").trim(),
            is_primary: !!c.is_primary,
          }))
          .filter((c) => c.name || c.mobile || c.email || c.pan || c.whatsapp),
      };
      const resp = await createDiagnosticLab(currentRole, payload);
      const lab = resp?.msg || resp;
      if (lab?.id) {
        let failed = (await uploadPhotos(lab.id)) + (await uploadDocuments(lab.id));
        if (hasOperationsInput(form.operations)) {
          try {
            await saveLabOperations(currentRole, lab.id, buildOperationsPayload(form.operations), false);
          } catch {
            failed += 1;
          }
        }
        if (failed) showToast("error", `Lab created — ${failed} item(s) failed to upload/save, retry on the detail page.`);
        navigate(`/subscriber-wallet/labs/labs/${lab.id}`, { state: { from: backTo } });
      } else {
        showToast("success", "Lab created.");
      }
    } catch (err) {
      showToast("error", err.message || "Failed to create lab.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div><h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to onboard labs.</p></div>
      </div>
    );
  }

  const errCls = (k, msg) => blur.errCls(k, msg, inputCls);
  const ie = identityErrors();

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(backTo)}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Onboard a Lab</h1>
            <p className="text-sm text-gray-400">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 px-4 py-4">
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const done = i < step, current = i === step;
              const Icon = s.icon;
              return (
                <React.Fragment key={s.key}>
                  <button type="button" onClick={() => i <= step && setStep(i)}
                    className={`flex items-center gap-2 ${i <= step ? "cursor-pointer" : "cursor-default"}`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                      done ? "bg-emerald-500 text-white" : current ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-400"}`}>
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </span>
                    <span className={`hidden md:block text-xs font-medium ${current ? "text-gray-900" : "text-gray-400"}`}>{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${i < step ? "bg-emerald-400" : "bg-gray-100"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-5">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
                {step === 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Brand name" required hint="shown to subscribers">
                      <input className={errCls("brand_name", ie.brand_name)} value={form.brand_name}
                        onChange={(e) => set("brand_name", e.target.value)} onBlur={() => blur.touch("brand_name")} />
                      {blur.msg("brand_name", ie.brand_name) && <p className="mt-1 text-xs text-red-500">{blur.msg("brand_name", ie.brand_name)}</p>}
                    </Field>
                    <Field label="Legal entity name" required hint="must match GSTIN">
                      <input className={errCls("business_name", ie.business_name)} value={form.business_name}
                        onChange={(e) => set("business_name", e.target.value)} onBlur={() => blur.touch("business_name")} />
                      {blur.msg("business_name", ie.business_name) && <p className="mt-1 text-xs text-red-500">{blur.msg("business_name", ie.business_name)}</p>}
                    </Field>

                    <div className="sm:col-span-2">
                      <Field label="Lab type" required>
                        <div onBlur={() => blur.touch("lab_type")} tabIndex={-1}>
                          <SegmentedControl value={form.lab_type} onChange={(v) => set("lab_type", v)} options={LAB_TYPE_OPTIONS} />
                        </div>
                        {blur.msg("lab_type", ie.lab_type) && <p className="mt-1 text-xs text-red-500">{blur.msg("lab_type", ie.lab_type)}</p>}
                      </Field>
                    </div>

                    <Field label="Year established">
                      <input type="text" inputMode="numeric" maxLength={4} className={errCls("established_year", ie.established_year)}
                        value={form.established_year}
                        onChange={(e) => set("established_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                        onBlur={() => blur.touch("established_year")}
                        placeholder="e.g. 1998" />
                      {blur.msg("established_year", ie.established_year) ? (
                        <p className="mt-1 text-xs text-red-500">{blur.msg("established_year", ie.established_year)}</p>
                      ) : form.established_year && (
                        <p className="mt-1 text-xs text-emerald-600 font-medium">
                          {Math.max(0, new Date().getFullYear() - Number(form.established_year))}+ Years Experience
                        </p>
                      )}
                    </Field>

                    <div className="sm:col-span-2">
                      <Field label="Chain" hint="optional — group under a brand">
                        <AsyncChainSelect role={currentRole} value={form.chain} label={form.chainLabel}
                          onChange={(id, lbl) => setForm((f) => ({ ...f, chain: id, chainLabel: lbl }))} />
                      </Field>
                    </div>

                    <div className="sm:col-span-2">
                      <Field label="Specializations">
                        <ChipMultiSelect options={SPECIALIZATION_OPTIONS} value={form.specializations} onChange={(v) => set("specializations", v)} />
                      </Field>
                    </div>

                    <div className="sm:col-span-2">
                      <Field
                        label="Modalities offered"
                        required={needsModalities}
                        hint={needsModalities ? "required for radiology / multi-modality" : "for radiology / multi-modality labs"}
                      >
                        <div onBlur={() => blur.touch("modalities")} tabIndex={-1}>
                          <ChipMultiSelect options={MODALITY_OPTIONS} value={form.modalities} onChange={(v) => set("modalities", v)} />
                        </div>
                        {blur.msg("modalities", ie.modalities) && <p className="mt-1 text-xs text-red-500">{blur.msg("modalities", ie.modalities)}</p>}
                      </Field>
                    </div>

                    <div className="sm:col-span-2">
                      <Field label="Languages spoken" required>
                        <div onBlur={() => blur.touch("languages")} tabIndex={-1}>
                          <ChipMultiSelect options={LANGUAGE_OPTIONS} value={form.languages} onChange={(v) => set("languages", v)} searchable creatable commonCount={5} />
                        </div>
                        {blur.msg("languages", ie.languages) && <p className="mt-1 text-xs text-red-500">{blur.msg("languages", ie.languages)}</p>}
                      </Field>
                    </div>

                    <div className="sm:col-span-2">
                      <Field label="Tagline" hint="≤ 80 characters">
                        <input className={errCls("tagline", ie.tagline)} value={form.tagline} maxLength={80}
                          onChange={(e) => set("tagline", e.target.value)} onBlur={() => blur.touch("tagline")}
                          placeholder="Short, punchy line shown to subscribers" />
                        <p className={`mt-1 text-xs ${blur.msg("tagline", ie.tagline) ? "text-red-500" : tagLen > 80 ? "text-red-500" : "text-gray-400"}`}>
                          {blur.msg("tagline", ie.tagline) || `${tagLen}/80`}
                        </p>
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Description" hint="≤ 500 characters">
                        <textarea rows={3} className={`${errCls("description", ie.description)} resize-none`} value={form.description} maxLength={500}
                          onChange={(e) => set("description", e.target.value)} onBlur={() => blur.touch("description")}
                          placeholder="About the lab" />
                        <p className={`mt-1 text-xs ${blur.msg("description", ie.description) ? "text-red-500" : descLen > 500 ? "text-red-500" : "text-gray-400"}`}>
                          {blur.msg("description", ie.description) || `${descLen}/500`}
                        </p>
                      </Field>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-5">
                    <Field label="Lab type" hint="how this lab is serviced">
                      <SegmentedControl
                        value={form.service_model}
                        onChange={(v) => set("service_model", v)}
                        options={[
                          { value: "local", label: "Local Lab" },
                          { value: "national", label: "National Lab" },
                        ]}
                      />
                      <p className="mt-1.5 text-xs text-gray-400">
                        {form.service_model === "national"
                          ? "Serviced by an uploaded set of serviceable pincodes (added after creation on the detail page)."
                          : "Serviced from a single address within a coverage radius."}
                      </p>
                    </Field>

                    {form.service_model === "national" ? (
                      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-2.5">
                        <MapPin className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Serviceable pincodes are added after creation.</p>
                          <p className="text-xs text-blue-700 mt-0.5">
                            National labs don&apos;t use a single address. Once the lab is created, upload its serviceable
                            pincode sheet on the lab&apos;s detail page to make it live in serviceability &amp; bookings.
                          </p>
                        </div>
                      </div>
                    ) : (
                    <>
                    <Field label="Address line" hint="street / building">
                      <input className={inputCls} value={form.address_line} onChange={(e) => set("address_line", e.target.value)} />
                    </Field>
                    <Field label="Landmark">
                      <input className={inputCls} value={form.landmark} onChange={(e) => set("landmark", e.target.value)} />
                    </Field>
                    <PincodeAutoAddress
                      blur={blur}
                      pincode={form.pincode}
                      locality={form.locality}
                      city={form.city}
                      state={form.state}
                      onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                    />
                    <div className="rounded-xl border border-gray-200 p-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                          <MapPin className="w-4 h-4" />
                        </span>
                        <div className="min-w-0">
                          {form.latitude && form.longitude ? (
                            <>
                              <p className="text-sm font-medium text-gray-900 truncate">{form.latitude}, {form.longitude}</p>
                              <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer"
                                className="text-[11px] text-blue-600 hover:underline">View on map ↗</a>
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-700">Coordinates not set</p>
                              <p className="text-[11px] text-gray-400">Auto-fetch from the address above (also derived on publish)</p>
                            </>
                          )}
                        </div>
                      </div>
                      <button type="button" onClick={handleGeocode} disabled={geoBusy}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors">
                        {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                        {form.latitude ? "Re-fetch lat/long" : "Auto-fetch lat/long"}
                      </button>
                    </div>

                    <LocationPicker
                      latitude={form.latitude}
                      longitude={form.longitude}
                      onChange={({ latitude, longitude }) => setForm((f) => ({ ...f, latitude, longitude }))}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Latitude" hint="editable">
                        <input className={errCls("latitude", latErr())} value={form.latitude} inputMode="decimal" placeholder="e.g. 28.6999318"
                          onChange={(e) => set("latitude", e.target.value.replace(/[^0-9.-]/g, ""))}
                          onBlur={() => blur.touch("latitude")} />
                        {blur.msg("latitude", latErr()) && <p className="mt-1 text-xs text-red-500">{blur.msg("latitude", latErr())}</p>}
                      </Field>
                      <Field label="Longitude" hint="editable">
                        <input className={errCls("longitude", lngErr())} value={form.longitude} inputMode="decimal" placeholder="e.g. 77.3329804"
                          onChange={(e) => set("longitude", e.target.value.replace(/[^0-9.-]/g, ""))}
                          onBlur={() => blur.touch("longitude")} />
                        {blur.msg("longitude", lngErr()) && <p className="mt-1 text-xs text-red-500">{blur.msg("longitude", lngErr())}</p>}
                      </Field>
                    </div>
                    </>
                    )}

                    {/* Contacts */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Contacts</p>
                          <p className="text-xs text-gray-400">Receptionist, SPOC, owner, phlebo… — add as many as you need.</p>
                        </div>
                        {form.contacts.length > 0 && (
                          <button onClick={addContact} className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Plus className="w-4 h-4" /> Add
                          </button>
                        )}
                      </div>

                      {form.contacts.length === 0 ? (
                        <button onClick={addContact}
                          className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-blue-600 border border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition-all">
                          <Plus className="w-4 h-4" /> Add a contact
                        </button>
                      ) : (
                        <div className="space-y-4">
                          {form.contacts.map((c, i) => {
                            const ce = contactFieldErrors(i, c);
                            return (
                            <div key={i} className="relative rounded-xl border border-gray-200 p-4 pt-3">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-semibold text-gray-400">Contact {i + 1}</span>
                                <button onClick={() => removeContact(i)} aria-label="Remove contact"
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Name" required>
                                  <input className={errCls(ck(i, "name"), ce[ck(i, "name")])} value={c.name}
                                    onChange={(e) => updateContact(i, "name", e.target.value)}
                                    onBlur={() => blur.touch(ck(i, "name"))} />
                                  {blur.msg(ck(i, "name"), ce[ck(i, "name")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "name"), ce[ck(i, "name")])}</p>}
                                </Field>
                                <Field label="Type">
                                  <select className={inputCls} value={c.type} onChange={(e) => updateContact(i, "type", e.target.value)}>
                                    <option value="">Select type</option>
                                    {CONTACT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                  </select>
                                </Field>
                                <Field label="Mobile" required>
                                  <input className={errCls(ck(i, "mobile"), ce[ck(i, "mobile")])} value={c.mobile} maxLength={10} inputMode="numeric"
                                    onChange={(e) => updateContact(i, "mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                                    onBlur={() => blur.touch(ck(i, "mobile"))} />
                                  {blur.msg(ck(i, "mobile"), ce[ck(i, "mobile")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "mobile"), ce[ck(i, "mobile")])}</p>}
                                </Field>
                                <Field label="Email">
                                  <input type="email" className={errCls(ck(i, "email"), ce[ck(i, "email")])} value={c.email}
                                    onChange={(e) => updateContact(i, "email", e.target.value)}
                                    onBlur={() => blur.touch(ck(i, "email"))} />
                                  {blur.msg(ck(i, "email"), ce[ck(i, "email")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "email"), ce[ck(i, "email")])}</p>}
                                </Field>
                                {CONTACT_TYPES_WITH_WHATSAPP.includes(c.type) && (
                                  <Field label="WhatsApp number">
                                    <input className={errCls(ck(i, "whatsapp"), ce[ck(i, "whatsapp")])} value={c.whatsapp} maxLength={10} inputMode="numeric"
                                      onChange={(e) => updateContact(i, "whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))}
                                      onBlur={() => blur.touch(ck(i, "whatsapp"))} />
                                    {blur.msg(ck(i, "whatsapp"), ce[ck(i, "whatsapp")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "whatsapp"), ce[ck(i, "whatsapp")])}</p>}
                                  </Field>
                                )}
                                {CONTACT_TYPES_WITH_PAN.includes(c.type) && (
                                  <Field label="PAN">
                                    <input className={errCls(ck(i, "pan"), ce[ck(i, "pan")], `${inputCls} uppercase`)} value={c.pan} maxLength={10}
                                      onChange={(e) => updateContact(i, "pan", e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 10))}
                                      onBlur={() => blur.touch(ck(i, "pan"))} />
                                    {blur.msg(ck(i, "pan"), ce[ck(i, "pan")]) && <p className="mt-1 text-xs text-red-500">{blur.msg(ck(i, "pan"), ce[ck(i, "pan")])}</p>}
                                  </Field>
                                )}
                              </div>
                              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={c.is_primary} onChange={(e) => updateContact(i, "is_primary", e.target.checked)}
                                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <span className="text-sm text-gray-600">Primary contact</span>
                              </label>
                            </div>
                          ); })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {step === 2 && (
                  <div className="space-y-4">
                    <div className="flex items-start gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>
                        Set collection modes, capacity, and weekly operating hours.
                        {" "}<span className="text-gray-400">Optional — leave both collection modes off to skip and configure later on the lab detail page.</span>
                      </p>
                    </div>
                    <OperationsForm
                      value={form.operations}
                      onChange={(v) => {
                        setForm((f) => ({ ...f, operations: v }));
                        if (opsTouched) setOpsErrors(validateOperations(v));
                      }}
                      errors={opsErrors}
                    />
                  </div>
                )}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <ShieldCheck className="w-4 h-4" /> Compliance documents — PDF / JPG / PNG, ≤10MB. They upload when you publish.
                    </div>
                    {LAB_DOC_TYPES.map((dt) => {
                      const isKyc = dt.multi;
                      const single = !isKyc ? form.documents[dt.key] : null;
                      const kyc = isKyc ? (form.documents.owner_kyc || []) : [];
                      const canAddMore = isKyc ? kyc.length < LAB_DOC_KYC_MAX : !single;
                      return (
                        <div key={dt.key} className="rounded-xl border border-gray-200 p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-gray-800">{dt.label}</p>
                                {dt.required && <span className="px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[11px] font-medium">Required</span>}
                              </div>
                              {dt.hint && <p className="text-[11px] text-gray-400 mt-0.5">{dt.hint}</p>}
                            </div>
                            {canAddMore && (
                              <label className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors">
                                <input type="file" accept={LAB_DOC_ACCEPT} className="hidden"
                                  onChange={(e) => { const fl = e.target.files?.[0]; e.target.value = ""; isKyc ? addKycDoc(fl) : setSingleDoc(dt.key, fl); }} />
                                <UploadCloud className="w-3.5 h-3.5" />
                                {isKyc ? "Add file" : single ? "Replace" : "Upload"}
                              </label>
                            )}
                          </div>
                          {(single || kyc.length > 0) && (
                            <ul className="space-y-1.5">
                              {(isKyc ? kyc : [single]).map((d, i) => (
                                <li key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                  <span className="text-sm text-gray-700 truncate flex-1">{d.name}</span>
                                  <button onClick={() => (isKyc ? removeKycDoc(i) : removeSingleDoc(dt.key))} aria-label="Remove"
                                    className="p-1 text-gray-400 hover:text-red-600 rounded">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {step === 4 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <ImageIcon className="w-4 h-4" /> All photos optional — logo &amp; exterior strongly boost subscriber conversion. They upload when you publish.
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {LAB_PHOTO_SLOTS.map((slot) => {
                        const p = form.photos[slot.key];
                        return (
                          <div key={slot.key} className="space-y-1.5">
                            <label className={`relative aspect-video rounded-xl border border-dashed ${p ? "border-gray-200" : "border-blue-200"} bg-gray-50 overflow-hidden flex items-center justify-center cursor-pointer hover:bg-blue-50/40 transition-colors group`}>
                              <input type="file" accept="image/png,image/jpeg" className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; setSinglePhoto(slot.key, f); }} />
                              {p && <img src={p.preview} alt={slot.label} className="w-full h-full object-cover" />}
                              <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 transition-all ${p ? "bg-black/0 group-hover:bg-black/40 text-transparent group-hover:text-white" : "text-blue-500"}`}>
                                <ImagePlus className="w-5 h-5" /><span className="text-[11px] font-medium">{p ? "Replace" : "Upload"}</span>
                              </div>
                            </label>
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-xs font-medium text-gray-700">{slot.label}</p>
                                <p className="text-[11px] text-gray-400">{slot.hint}</p>
                              </div>
                              {p && (
                                <button onClick={() => removeSinglePhoto(slot.key)} aria-label="Remove" className="mt-0.5 p-1 text-gray-400 hover:text-red-600 rounded-lg">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-5 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Additional gallery</p>
                          <p className="text-[11px] text-gray-400">PNG / JPEG · ≤2MB each</p>
                        </div>
                        <span className="text-xs text-gray-400">{form.gallery.length}/{LAB_GALLERY_MAX}</span>
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {form.gallery.map((g, i) => (
                          <div key={i} className="relative aspect-square rounded-xl border border-gray-200 overflow-hidden group">
                            <img src={g.preview} alt="Gallery" className="w-full h-full object-cover" />
                            <button onClick={() => removeGalleryPhoto(i)} aria-label="Remove photo"
                              className="absolute top-1 right-1 p-1 rounded-lg bg-white/90 text-gray-500 hover:text-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        {form.gallery.length < LAB_GALLERY_MAX && (
                          <label className="aspect-square rounded-xl border border-dashed border-blue-200 bg-gray-50 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-blue-50/40 text-blue-500 transition-colors">
                            <input type="file" accept="image/png,image/jpeg" className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addGalleryPhoto(f); }} />
                            <Plus className="w-5 h-5" /><span className="text-[11px] font-medium">Add</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <ClipboardCheck className="w-4 h-4" /> Review the captured Identity, then publish.
                    </div>
                    <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                      {[
                        ["Legal entity", form.business_name || "—"],
                        ["Brand name", form.brand_name || "—"],
                        ["Lab type", labelFor(LAB_TYPE_OPTIONS, form.lab_type)],
                        ["Year established", form.established_year || "—"],
                        ["Chain", form.chainLabel || "Standalone"],
                        ["Specializations", form.specializations.map((v) => labelFor(SPECIALIZATION_OPTIONS, v)).join(", ") || "—"],
                        ...(form.modalities.length ? [["Modalities", form.modalities.map((v) => labelFor(MODALITY_OPTIONS, v)).join(", ")]] : []),
                        ["Languages", form.languages.map((v) => labelFor(LANGUAGE_OPTIONS, v)).join(", ") || "—"],
                        ["Tagline", form.tagline || "—"],
                        ["Contacts", form.contacts.length ? `${form.contacts.length} added` : "—"],
                        ["Lab type", form.service_model === "national" ? "National Lab" : "Local Lab"],
                        form.service_model === "national"
                          ? ["Serviceable pincodes", "Upload on detail page after creation"]
                          : ["Address", [form.locality, form.city, form.pincode].filter(Boolean).join(", ") || "—"],
                        ["Operations", opsReviewLabel()],
                        ["Photos", (Object.keys(form.photos).length + form.gallery.length) ? `${Object.keys(form.photos).length + form.gallery.length} selected` : "—"],
                        ["Documents", docCount ? `${docCount} selected` : "—"],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-start justify-between px-4 py-2.5 gap-4">
                          <span className="text-xs text-gray-400 uppercase tracking-wider">{k}</span>
                          <span className="text-sm text-gray-800 text-right">{v}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-4">
                      <button type="button" onClick={() => setStep(0)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">← Edit Identity</button>
                      <button type="button" onClick={() => setStep(2)} className="text-sm text-blue-600 hover:text-blue-700 font-medium">← Edit Operations</button>
                    </div>
                    <p className="text-xs text-gray-400">The lab will be created with <span className="font-medium text-amber-600">verification pending</span> until compliance review is complete.</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer nav */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              className={`px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${step === 0 ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border border-gray-300"}`}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={goNext} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5 transition-colors">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handlePublish} disabled={submitting}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? "Publishing…" : "Publish lab"}
              </button>
            )}
          </div>
        </div>
      </div>
      <Toast toast={toast} />
    </div>
  );
};

export default DiagnosticLabWizard;
